import React, { FC } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Container, Spinner } from 'reactstrap';

import PageSpinner from '../../components/PageSpinner';
import Helmet from '../../components/common/Helmet';
import InspirationItemMain from '../../components/inspirations/InspirationItemMain';
import InspirationList from '../../components/inspirations/InspirationList';
import InspirationNavBar from '../../components/inspirations/InspirationNavBar';
import classNames from '../../helpers/classNames';
import useTranslate from '../../hooks/useTranslate';
import { useInspirationsInfiniteQuery } from '../../services/apis/inspirations/api';
import isValidInspirationCategory from '../../services/apis/inspirations/isValidInspirationCategory';
import { InspirationType } from '../../services/apis/inspirations/types';

const InspirationsPage: FC = () => {
  const { translate, activeLanguage } = useTranslate();
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category') as InspirationType['category'] | null;
  const category =
    categoryParam && isValidInspirationCategory(categoryParam) ? categoryParam : undefined;
  const filters = {
    category,
    language: activeLanguage.code,
  };
  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } =
    useInspirationsInfiniteQuery(filters);
  const { pages = [] } = data ?? {};
  const [firstInspiration, ...otherInspirations] = pages.flat();
  const restInspirations = otherInspirations.filter(Boolean) as InspirationType[];

  return (
    <>
      <Helmet
        titleTranslationKey="Inspirations.title"
        descriptionTranslationKey="Inspirations.description"
      />
      <Container className="px-3 pt-3 pb-5 min-vh-100">
        <h1 className="mb-4">{String(translate('Inspirations.title'))}</h1>

        <InspirationNavBar activeCategory={category} className="mb-5" />

        {isLoading ? (
          <PageSpinner />
        ) : (
          <>
            {firstInspiration && <InspirationItemMain inspiration={firstInspiration} />}

            {restInspirations?.length ? <InspirationList inspirations={restInspirations} /> : null}

            {hasNextPage && (
              <Button
                className="mb-5 px-5 d-block mx-auto position-relative"
                disabled={isFetching}
                onClick={() => fetchNextPage()}
              >
                <span className={classNames(isFetching ? 'invisible' : '')}>
                  {String(translate('Inspirations.loadMoreCta'))}
                </span>
                {isFetching && (
                  <div className="position-absolute top-50 start-50 translate-middle">
                    <Spinner size="sm" />
                  </div>
                )}
              </Button>
            )}
          </>
        )}
      </Container>
    </>
  );
};

export default InspirationsPage;
